import { CalendarDate, parseDate } from '@internationalized/date'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/sl'

import i18n from '@/libs/i18n'

dayjs.extend(utc)
dayjs.extend(customParseFormat)
dayjs.extend(isSameOrBefore)

export default dayjs

/** The wire format for a calendar date. A stay date is a day, never an instant. */
export const DATE_FORMAT = 'YYYY-MM-DD'

export const formatDate = (value?: string | null): string =>
    value ? dayjs(value).format('DD.MM.YYYY') : ''

export const formatDateTime = (value?: string | null): string =>
    value ? dayjs(value).format('DD.MM.YYYY HH:mm') : ''

/** The hour of an instant, for a list read within one day of it happening. */
export const formatTime = (value?: string | null): string =>
    value ? dayjs(value).format('HH:mm') : ''

/**
 * Whether an OPTIONAL instant off the wire is actually set.
 *
 * Absent is easy; the trap is the other spelling. Go's zero `time.Time` is a real
 * instant — `0001-01-01T00:00:00Z` — and a service that writes one out hands the browser
 * a NON-EMPTY string, which is truthy, parses cleanly, and formats. On 2026-09-06 „Moja
 * ura" therefore printed „odhod 01:22" for a departure that never happened, over the line
 * saying the day was not finished: Ljubljana is linked to Belgrade in tzdata and
 * Belgrade's LMT offset is +01:22, so that is what the year 1 looks like in this house.
 *
 * The API no longer sends it (DataStructures.StaffTimeDay.MarshalJSON), and this stays
 * because a screen must not be the only thing standing between a zero value and a person
 * reading their own working-time record — a portal is cached, a service is deployed, and
 * the two are never updated in the same second.
 *
 * The year and not the string: a value written by any other producer, in any offset, is
 * still the year 1.
 */
export const instantSet = (value?: string | null): boolean => {
    if (!value) {
        return false
    }

    const at = dayjs(value)

    return at.isValid() && at.year() > 1
}

/**
 * A day named the way somebody reads a forecast: "petek, 29. 8.".
 *
 * The weekday is what makes a forecast readable — a kitchen orders for Saturday, not
 * for the twenty-ninth — and the date is beside it because a weekday alone is
 * ambiguous the moment the window is longer than a week.
 *
 * Named in the language the person reading it chose, which is why the locale comes
 * from i18n rather than being fixed: two receptionists share one desk and each reads
 * the interface in their own (docs/PLAN_GOST.md — the same rule the rest of the portal
 * follows).
 */
export const formatWeekday = (value?: string | null): string =>
    value ? dayjs(value).locale(String(i18n.global.locale.value)).format('dddd, D. M.') : ''

/**
 * A day with no year on it: "5. 9." — the date half of formatWeekday, and written
 * through the same locale for the same reason.
 *
 * It exists for sentences that compare two days a few nights apart, where the year is
 * four characters of noise repeated twice: "Prihod: 5. 9. → 6. 9." is read at a
 * glance, and "05.09.2026 → 06.09.2026" is read one digit at a time. Nothing that
 * stands ALONE may use it — a date with no year is only unambiguous next to another
 * one — which is why formatDate stays the portal's default.
 */
export const formatDayMonth = (value?: string | null): string =>
    value ? dayjs(value).locale(String(i18n.global.locale.value)).format('D. M.') : ''

/**
 * Serializes a date for the API. Dates are sent as plain YYYY-MM-DD, never as an
 * ISO instant: the server stores a room-night as a calendar day at UTC midnight, and
 * sending 22:00+02:00 would file a booking on the wrong night for half the year.
 */
export const toApiDate = (value: CalendarDate | string | Date): string =>
    value instanceof CalendarDate ? value.toString() : dayjs(value).format(DATE_FORMAT)

/** Converts an API date into the value type Nuxt UI's date components expect. */
export const toCalendarDate = (value?: string | null): CalendarDate | undefined =>
    value ? parseDate(dayjs(value).format(DATE_FORMAT)) : undefined

export const today = (): CalendarDate => parseDate(dayjs().format(DATE_FORMAT))

/** Nights between arrival and departure — departure excluded, as everywhere else. */
export const nightCount = (arrival?: string | null, departure?: string | null): number =>
    arrival && departure ? Math.max(0, dayjs(departure).diff(dayjs(arrival), 'day')) : 0

/** Every date of a range, inclusive, as YYYY-MM-DD. Used to build the chessboard. */
/** A calendar date, so many days later. Days rather than hours: a day is not always 24 of them. */
export const addDays = (date: string, days: number): string =>
    dayjs(date).add(days, 'day').format(DATE_FORMAT)

export const dateRange = (from: string, to: string): string[] => {
    const dates: string[] = []

    let cursor = dayjs(from)
    const end = dayjs(to)

    while (cursor.isSameOrBefore(end, 'day')) {
        dates.push(cursor.format(DATE_FORMAT))
        cursor = cursor.add(1, 'day')
    }

    return dates
}
